import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Component, ChangeDetectionStrategy, ViewEncapsulation, Input, NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import * as i2 from '@angular/material/table';
import { MatTableModule } from '@angular/material/table';
import * as i1$1 from '@sick-curie/dashboard-builder-core';
import { SpecializedElementPropertiesComponent, DashboardBuilderCoreModule } from '@sick-curie/dashboard-builder-core';
import { __decorate, __metadata } from 'tslib';
import { Describe, AngularDashboardElement, ElementCategory, AngularPropertiesElement } from '@sick-curie/dashboard-builder-ui-models';
import { BehaviorSubject } from 'rxjs';
import { distinct, map } from 'rxjs/operators';
import '@sick-davinci/basic-elements/lib/text-field/TextField';
import '@sick-davinci/basic-elements/lib/numeric-field/NumericField';
import '@sick-davinci/basic-elements/lib/checkbox/Checkbox';

var GenericTableComponent_1;
const equals = (d1, d2) => {
    if ((!d1 && d2) || (d1 && !d2)) {
        return false;
    }
    if (!d1 && !d2) {
        return true;
    }
    const keys1 = Object.keys(d1);
    const keys2 = Object.keys(d2);
    if (keys1.length !== keys2.length) {
        return false;
    }
    for (const key of keys1) {
        if (d1[key] !== d2[key]) {
            return false;
        }
    }
    return true;
};
const trimAll = (arr) => arr.map((el) => el.trim());
const distinctArr = (arr) => Array.from(arr.reduce((prev, curr) => prev.add(curr), new Set()).values());
const prepareHeaderNames = (arr) => distinctArr(trimAll(arr)).filter(Boolean);
let GenericTableComponent = GenericTableComponent_1 = class GenericTableComponent {
    constructor() {
        this.columns$ = new BehaviorSubject(['FirstName', 'Name']);
        this.rowsData = new BehaviorSubject({
            maxRows: 30,
            inverseOrder: false,
            rows: [{ FirstName: 'Max', Name: 'Imilian' }],
        });
        this.rows = this.rowsData.pipe(distinct(), map(({ rows, maxRows, inverseOrder }) => {
            const currentRows = [...rows].slice(-1 * GenericTableComponent_1.getMaximumRows(maxRows));
            return inverseOrder ? currentRows.reverse() : currentRows;
        }));
        this._rowDirect = '';
        this._maxRows = 30;
        this._inverseOrder = false;
        this._mode = 'append';
    }
    get maxRows() {
        return this._maxRows;
    }
    set maxRows(value) {
        if (value !== this._maxRows) {
            this.rowsData.next({
                ...this.rowsData.value,
                maxRows: value,
            });
        }
        this._maxRows = value;
    }
    get inverseOrder() {
        return this._inverseOrder;
    }
    /**
     * true: newest lines are on top
     * false (default): append rows on the bottom
     */
    set inverseOrder(value) {
        if (value !== this._inverseOrder) {
            this.rowsData.next({
                ...this.rowsData.value,
                inverseOrder: value,
            });
        }
        this._inverseOrder = value;
    }
    get mode() {
        return this._mode;
    }
    set mode(value) {
        if (value === 'replace' || value === 'append') {
            this._mode = value;
        }
    }
    get headerNames() {
        return this.columns$.value.join(', ');
    }
    set headerNames(values) {
        const columns = values.split(',').map((part) => part.trim());
        this.columns$.next(prepareHeaderNames(columns));
        this._removeNonFittingRows(columns);
    }
    get row() {
        if (!this.rowsData.value.rows.length) {
            return '';
        }
        const { rows } = this.rowsData.value;
        return JSON.stringify(rows[rows.length - 1]);
    }
    set row(data) {
        let dataParsed = data;
        if (typeof data === 'string') {
            if (!data.length) {
                return;
            }
            try {
                dataParsed = JSON.parse(data);
            }
            catch (e) {
                this._set({ [this.columns$.value[0] || 'error']: `Failed to parse input: "${data}".` }, this.mode);
                return;
            }
        }
        if (Array.isArray(dataParsed)) {
            if (dataParsed.length === 0) {
                return;
            }
            if (typeof dataParsed[0] === 'object') {
                this._set(dataParsed, this.mode);
                return;
            }
        }
        else if (typeof dataParsed === 'object') {
            this._setUnique(dataParsed, this.mode);
            return;
        }
        this._set({
            [this.columns$.value[0] || 'error']: `unsupported data type for table data: "${dataParsed}".`,
        }, this.mode);
    }
    // secret input for the last manually entered row in settings dialog
    set rowDirect(data) {
        this._rowDirect = data;
    }
    get rowDirect() {
        return this._rowDirect === '' ? this.row : this._rowDirect;
    }
    static getMaximumRows(maxRows) {
        return Math.max(maxRows, 1);
    }
    _removeNonFittingRows(columns) {
        // filter old rows and only keep the ones with fitting entries
        const { rows } = this.rowsData.value;
        const validRows = rows.filter((row) => {
            const keys = Object.keys(row);
            for (const key of keys) {
                if (columns.includes(key)) {
                    return true;
                }
            }
            return false;
        });
        if (rows.length !== validRows.length) {
            this.rowsData.next({
                ...this.rowsData.value,
                rows: validRows,
            });
        }
    }
    _setUnique(data, insertMode) {
        const { rows } = this.rowsData.value;
        const lastRow = rows.length && rows[rows.length - 1];
        if (!equals(lastRow, data)) {
            this._set(data, insertMode);
        }
    }
    _set(data, insertMode) {
        let newRows = [...(Array.isArray(data) ? data : [data])];
        if (insertMode === 'append') {
            newRows = [...this.rowsData.value.rows, ...newRows];
        }
        this.rowsData.next({
            ...this.rowsData.value,
            // preserve more rows than needed, so we are able to show historic rows when maxRows increases
            rows: newRows.slice(-1 * GenericTableComponent_1.getMaximumRows(this.maxRows) * 2),
        });
    }
};
GenericTableComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: GenericTableComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
GenericTableComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.0.0", type: GenericTableComponent, selector: "dashboard-generic-table", inputs: { maxRows: "maxRows", inverseOrder: "inverseOrder", mode: "mode", headerNames: "headerNames", row: "row", rowDirect: "rowDirect" }, ngImport: i0, template: "<table mat-table [dataSource]=\"rows\" *ngIf=\"columns$ | async as columns\">\n  <ng-container [matColumnDef]=\"column\" *ngFor=\"let column of columns\">\n    <th mat-header-cell *matHeaderCellDef>{{ column }}</th>\n    <td mat-cell *matCellDef=\"let element\">{{ element[column] }}</td>\n  </ng-container>\n\n  <tr mat-header-row *matHeaderRowDef=\"columns\"></tr>\n  <tr mat-row *matRowDef=\"let row; columns: columns\"></tr>\n</table>\n", styles: [":host{width:100%;height:100%;padding:1rem 1.5rem 1.5rem;box-sizing:border-box;overflow:auto}:host .mat-table{width:100%}:host .mat-table .mat-header-cell,:host .mat-table .mat-cell{color:var(--davinci-color-text-light,#50585C);font-weight:400;border-color:var(--davinci-color-mid-gray-4,#d9dcde)}:host .mat-table .mat-header-cell+.mat-header-cell,:host .mat-table .mat-cell+.mat-cell{padding-left:1em;border-left:1px solid var(--davinci-color-light-gray-1,#EEEFF0)}:host .mat-table .mat-header-cell{color:var(--davinci-color-text-lighter,#737F85);border-bottom-width:2px;font-weight:600}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2.MatTable, selector: "mat-table, table[mat-table]", exportAs: ["matTable"] }, { kind: "directive", type: i2.MatHeaderCellDef, selector: "[matHeaderCellDef]" }, { kind: "directive", type: i2.MatHeaderRowDef, selector: "[matHeaderRowDef]", inputs: ["matHeaderRowDef", "matHeaderRowDefSticky"] }, { kind: "directive", type: i2.MatColumnDef, selector: "[matColumnDef]", inputs: ["sticky", "matColumnDef"] }, { kind: "directive", type: i2.MatCellDef, selector: "[matCellDef]" }, { kind: "directive", type: i2.MatRowDef, selector: "[matRowDef]", inputs: ["matRowDefColumns", "matRowDefWhen"] }, { kind: "directive", type: i2.MatHeaderCell, selector: "mat-header-cell, th[mat-header-cell]" }, { kind: "directive", type: i2.MatCell, selector: "mat-cell, td[mat-cell]" }, { kind: "component", type: i2.MatHeaderRow, selector: "mat-header-row, tr[mat-header-row]", exportAs: ["matHeaderRow"] }, { kind: "component", type: i2.MatRow, selector: "mat-row, tr[mat-row]", exportAs: ["matRow"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.ShadowDom });
__decorate([
    Describe({ mandatory: true }),
    __metadata("design:type", Number),
    __metadata("design:paramtypes", [Number])
], GenericTableComponent.prototype, "maxRows", null);
__decorate([
    Describe({ defaultValue: false, mandatory: true, description: 'When true: invert the vertical order of the rows.' }),
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [Boolean])
], GenericTableComponent.prototype, "inverseOrder", null);
__decorate([
    Describe({
        defaultValue: 'append',
        mandatory: true,
        description: 'Replace content with new data or append the received data',
    }),
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [Object])
], GenericTableComponent.prototype, "mode", null);
__decorate([
    Describe({ mandatory: true, defaultValueType: 'string', defaultValue: 'FirstName, Name' }),
    __metadata("design:type", String),
    __metadata("design:paramtypes", [String])
], GenericTableComponent.prototype, "headerNames", null);
__decorate([
    Describe({ mandatory: true, type: 'any' }),
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [Object])
], GenericTableComponent.prototype, "row", null);
GenericTableComponent = GenericTableComponent_1 = __decorate([
    AngularDashboardElement({
        role: 'ui',
        documentation: {
            name: 'Generic Table',
            description: 'Appends data to a table',
            category: ElementCategory.Tables,
            initialHeight: 7,
            initialWidth: 7,
        },
    })
], GenericTableComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: GenericTableComponent, decorators: [{
            type: Component,
            args: [{ selector: 'dashboard-generic-table', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.ShadowDom, template: "<table mat-table [dataSource]=\"rows\" *ngIf=\"columns$ | async as columns\">\n  <ng-container [matColumnDef]=\"column\" *ngFor=\"let column of columns\">\n    <th mat-header-cell *matHeaderCellDef>{{ column }}</th>\n    <td mat-cell *matCellDef=\"let element\">{{ element[column] }}</td>\n  </ng-container>\n\n  <tr mat-header-row *matHeaderRowDef=\"columns\"></tr>\n  <tr mat-row *matRowDef=\"let row; columns: columns\"></tr>\n</table>\n", styles: [":host{width:100%;height:100%;padding:1rem 1.5rem 1.5rem;box-sizing:border-box;overflow:auto}:host .mat-table{width:100%}:host .mat-table .mat-header-cell,:host .mat-table .mat-cell{color:var(--davinci-color-text-light,#50585C);font-weight:400;border-color:var(--davinci-color-mid-gray-4,#d9dcde)}:host .mat-table .mat-header-cell+.mat-header-cell,:host .mat-table .mat-cell+.mat-cell{padding-left:1em;border-left:1px solid var(--davinci-color-light-gray-1,#EEEFF0)}:host .mat-table .mat-header-cell{color:var(--davinci-color-text-lighter,#737F85);border-bottom-width:2px;font-weight:600}\n"] }]
        }], propDecorators: { maxRows: [{
                type: Input
            }], inverseOrder: [{
                type: Input
            }], mode: [{
                type: Input
            }], headerNames: [{
                type: Input
            }], row: [{
                type: Input
            }], rowDirect: [{
                type: Input
            }] } });

var GenericTablePropertiesComponent_1;
let GenericTablePropertiesComponent = GenericTablePropertiesComponent_1 = class GenericTablePropertiesComponent extends SpecializedElementPropertiesComponent {
    constructor() {
        super(...arguments);
        this.row = '';
        this.addRow = false;
        this.exampleDataSingle = {};
        this.exampleDataMulti = [];
    }
    static buildExampleData(headerNames) {
        const headerArray = headerNames.split(',');
        if (!headerNames) {
            return {
                exampleSingle: {
                    Warning: 'Please specify a header!',
                },
                exampleMulti: [
                    {
                        Warning: 'Please specify a header!',
                    },
                ],
            };
        }
        const exampleSingle = {};
        const exampleMulti = [{}, {}];
        const headerNamesSafe = prepareHeaderNames(headerArray);
        headerNamesSafe.forEach((key, index) => {
            exampleSingle[key] = `Example data ${index}`;
            exampleMulti.forEach((row, rowIndex) => {
                row[key] = `Example data ${rowIndex * headerNamesSafe.length + index}`;
            });
        });
        return { exampleSingle, exampleMulti };
    }
    ngOnInit() {
        this.updateExampleRow(this.element?.properties?.headerNames || '');
        this.row = this.element?.properties?.rowDirect ?? this.element?.properties?.row ?? '';
        this.storeRow();
    }
    updateExampleRow(headerNames) {
        const { exampleSingle, exampleMulti } = GenericTablePropertiesComponent_1.buildExampleData(headerNames);
        this.exampleDataSingle = exampleSingle;
        this.exampleDataMulti = exampleMulti;
    }
    onRowInput(value) {
        this.row = value;
        this.addRow = true;
        this.storeRow();
    }
    onAddRowChange(checked) {
        this.addRow = checked;
        this.storeRow();
    }
    storeRow() {
        let newData;
        try {
            newData = this.addRow ? JSON.parse(this.row) : [];
            newData =
                Array.isArray(newData) && (newData.length === 0 || (newData.length > 0 && typeof newData[0] === 'object'))
                    ? newData
                    : [newData];
        }
        catch (e) {
            // invalid JSON - pass as string to show warning or clear data if adding disabled
            newData = this.addRow ? this.row : [];
        }
        this.propertyChange.emit({
            property: 'row',
            value: newData,
        });
        this.propertyChange.emit({
            property: 'rowDirect',
            value: this.row,
        });
    }
};
GenericTablePropertiesComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: GenericTablePropertiesComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
GenericTablePropertiesComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.0.0", type: GenericTablePropertiesComponent, selector: "dashboard-generic-table-properties", usesInheritance: true, ngImport: i0, template: "<div class=\"grid-columns-4\">\n  <div class=\"label\">Maximum number of rows</div>\n  <davinci-numeric-field [min]=\"1\" [linkToElement]=\"element\" linkToProperty=\"maxRows\"></davinci-numeric-field>\n</div>\n<div class=\"grid-columns-2\">\n  <div class=\"label\">Inverse order of rows</div>\n  <div>\n    <davinci-checkbox [linkToElement]=\"element\" linkToProperty=\"inverseOrder\"></davinci-checkbox>\n  </div>\n</div>\n<div class=\"grid-columns-2\">\n  <div class=\"label\">Header names (comma separated)</div>\n  <davinci-text-field\n    [linkToElement]=\"element\"\n    required\n    linkToProperty=\"headerNames\"\n    (keyup)=\"updateExampleRow($event.target.value)\"\n  ></davinci-text-field>\n</div>\n<div class=\"grid-columns-2\">\n  <div class=\"align-baseline\">\n    You can fill the table by adding one or multiple rows at a time.<br />\n    Each row is an JavaScript object or array.<br />\n    The keys of this object must match the header names.<br />\n    The header names must be the indices if you want to use arrays as rows.\n  </div>\n  <div class=\"align-baseline\">\n    Example single row:\n    <pre class=\"example-object\"><code>{{ exampleDataSingle | json }}</code></pre>\n    Example multiple rows:\n    <pre class=\"example-object\"><code>{{ exampleDataMulti | json }}</code></pre>\n  </div>\n</div>\n<div class=\"grid-columns-2\">\n  <div class=\"label\">Row:</div>\n  <div class=\"row-input\">\n    <dashboard-builder-bindable>\n      <davinci-text-field [linkToElement]=\"element\" linkToProperty=\"row\" class=\"bindable-field\"></davinci-text-field>\n      <davinci-text-field [value]=\"row\" (input)=\"onRowInput($event.detail)\"></davinci-text-field>\n    </dashboard-builder-bindable>\n    <div class=\"add-row\">\n      <davinci-checkbox [checked]=\"addRow\" (change)=\"onAddRowChange($event.detail)\">add row on save</davinci-checkbox>\n    </div>\n  </div>\n</div>\n<div class=\"grid-columns-2\">\n  <div class=\"label\" helptext [element]=\"element\" property=\"mode\">Mode</div>\n  <div>\n    <davinci-toggle-group [linkToElement]=\"element\" linkToProperty=\"mode\" #autoLink=\"propertyLink\">\n      <davinci-radio-button [value]=\"'append'\"><span class=\"radio-label\">Append</span></davinci-radio-button>\n      <davinci-radio-button [value]=\"'replace'\"><span class=\"radio-label\">Replace</span></davinci-radio-button>\n    </davinci-toggle-group>\n  </div>\n</div>\n", styles: [":host>*:last-child{margin-bottom:2em}davinci-text-field,davinci-drop-down,davinci-numeric-field,davinci-text-area{margin:0;width:100%}davinci-text-area{height:8.57143rem}dashboard-builder-bindable{width:100%}davinci-callout{margin-bottom:1.6em}.grid-columns-2,.grid-columns-4{position:relative;margin-bottom:10px}@media screen and (min-width: 993px){.grid-columns-2,.grid-columns-4{display:grid;gap:10px 40px;align-items:center}}@media screen and (min-width: 993px){.grid-columns-2{grid-template-columns:120px 1fr}}.grid-columns-2 davinci-checkbox+.label{margin-left:16px}@media screen and (max-width: 992px){.grid-columns-4 .label:nth-child(3n){margin-top:10px}}@media screen and (min-width: 993px){.grid-columns-4{grid-template-columns:120px 1fr 80px 1fr}}.label{display:inline-flex}@media screen and (max-width: 992px){.label{margin-bottom:8px;padding-right:16px}}.label.disabled{opacity:.5}@media screen and (min-width: 993px){.grid-columns-2{grid-template-columns:200px 1fr}}@media screen and (min-width: 993px){.grid-columns-4{grid-template-columns:200px 1fr 80px 1fr}}.example-object{color:#a7a7a7}.row-input{width:100%}.row-input davinci-text-field{width:100%}.row-input davinci-text-field.bindable-field{display:none}.row-input dashboard-builder-bindable.is-selected+.add-row{display:none}.align-baseline{align-self:baseline}.radio-label{margin-right:20px}\n"], dependencies: [{ kind: "directive", type: i1$1.ElementPropertyLinkingDirective, selector: "[linkToElement][linkToProperty]", inputs: ["linkToElement", "linkToProperty", "linkToIndex", "currentValue"], outputs: ["currentValueChange"], exportAs: ["propertyLink"] }, { kind: "directive", type: i1$1.NumericFieldValueDirective, selector: "davinci-numeric-field", outputs: ["numericValueChange"] }, { kind: "component", type: i1$1.BindingStateComponent, selector: "dashboard-builder-bindable", inputs: ["bound", "overlayText", "helpTooltipVPos"] }, { kind: "component", type: i1$1.HelpStateComponent, selector: "[helptext]", inputs: ["element", "property", "helptext", "hPos", "vPos"] }, { kind: "pipe", type: i1.JsonPipe, name: "json" }] });
GenericTablePropertiesComponent = GenericTablePropertiesComponent_1 = __decorate([
    AngularPropertiesElement
], GenericTablePropertiesComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: GenericTablePropertiesComponent, decorators: [{
            type: Component,
            args: [{ selector: 'dashboard-generic-table-properties', template: "<div class=\"grid-columns-4\">\n  <div class=\"label\">Maximum number of rows</div>\n  <davinci-numeric-field [min]=\"1\" [linkToElement]=\"element\" linkToProperty=\"maxRows\"></davinci-numeric-field>\n</div>\n<div class=\"grid-columns-2\">\n  <div class=\"label\">Inverse order of rows</div>\n  <div>\n    <davinci-checkbox [linkToElement]=\"element\" linkToProperty=\"inverseOrder\"></davinci-checkbox>\n  </div>\n</div>\n<div class=\"grid-columns-2\">\n  <div class=\"label\">Header names (comma separated)</div>\n  <davinci-text-field\n    [linkToElement]=\"element\"\n    required\n    linkToProperty=\"headerNames\"\n    (keyup)=\"updateExampleRow($event.target.value)\"\n  ></davinci-text-field>\n</div>\n<div class=\"grid-columns-2\">\n  <div class=\"align-baseline\">\n    You can fill the table by adding one or multiple rows at a time.<br />\n    Each row is an JavaScript object or array.<br />\n    The keys of this object must match the header names.<br />\n    The header names must be the indices if you want to use arrays as rows.\n  </div>\n  <div class=\"align-baseline\">\n    Example single row:\n    <pre class=\"example-object\"><code>{{ exampleDataSingle | json }}</code></pre>\n    Example multiple rows:\n    <pre class=\"example-object\"><code>{{ exampleDataMulti | json }}</code></pre>\n  </div>\n</div>\n<div class=\"grid-columns-2\">\n  <div class=\"label\">Row:</div>\n  <div class=\"row-input\">\n    <dashboard-builder-bindable>\n      <davinci-text-field [linkToElement]=\"element\" linkToProperty=\"row\" class=\"bindable-field\"></davinci-text-field>\n      <davinci-text-field [value]=\"row\" (input)=\"onRowInput($event.detail)\"></davinci-text-field>\n    </dashboard-builder-bindable>\n    <div class=\"add-row\">\n      <davinci-checkbox [checked]=\"addRow\" (change)=\"onAddRowChange($event.detail)\">add row on save</davinci-checkbox>\n    </div>\n  </div>\n</div>\n<div class=\"grid-columns-2\">\n  <div class=\"label\" helptext [element]=\"element\" property=\"mode\">Mode</div>\n  <div>\n    <davinci-toggle-group [linkToElement]=\"element\" linkToProperty=\"mode\" #autoLink=\"propertyLink\">\n      <davinci-radio-button [value]=\"'append'\"><span class=\"radio-label\">Append</span></davinci-radio-button>\n      <davinci-radio-button [value]=\"'replace'\"><span class=\"radio-label\">Replace</span></davinci-radio-button>\n    </davinci-toggle-group>\n  </div>\n</div>\n", styles: [":host>*:last-child{margin-bottom:2em}davinci-text-field,davinci-drop-down,davinci-numeric-field,davinci-text-area{margin:0;width:100%}davinci-text-area{height:8.57143rem}dashboard-builder-bindable{width:100%}davinci-callout{margin-bottom:1.6em}.grid-columns-2,.grid-columns-4{position:relative;margin-bottom:10px}@media screen and (min-width: 993px){.grid-columns-2,.grid-columns-4{display:grid;gap:10px 40px;align-items:center}}@media screen and (min-width: 993px){.grid-columns-2{grid-template-columns:120px 1fr}}.grid-columns-2 davinci-checkbox+.label{margin-left:16px}@media screen and (max-width: 992px){.grid-columns-4 .label:nth-child(3n){margin-top:10px}}@media screen and (min-width: 993px){.grid-columns-4{grid-template-columns:120px 1fr 80px 1fr}}.label{display:inline-flex}@media screen and (max-width: 992px){.label{margin-bottom:8px;padding-right:16px}}.label.disabled{opacity:.5}@media screen and (min-width: 993px){.grid-columns-2{grid-template-columns:200px 1fr}}@media screen and (min-width: 993px){.grid-columns-4{grid-template-columns:200px 1fr 80px 1fr}}.example-object{color:#a7a7a7}.row-input{width:100%}.row-input davinci-text-field{width:100%}.row-input davinci-text-field.bindable-field{display:none}.row-input dashboard-builder-bindable.is-selected+.add-row{display:none}.align-baseline{align-self:baseline}.radio-label{margin-right:20px}\n"] }]
        }] });

class DashboardBuilderGenericTableElementModule {
}
DashboardBuilderGenericTableElementModule.REGISTER = [GenericTableComponent, GenericTablePropertiesComponent];
DashboardBuilderGenericTableElementModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: DashboardBuilderGenericTableElementModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DashboardBuilderGenericTableElementModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.0.0", ngImport: i0, type: DashboardBuilderGenericTableElementModule, declarations: [GenericTableComponent, GenericTablePropertiesComponent], imports: [CommonModule, MatTableModule, DashboardBuilderCoreModule] });
DashboardBuilderGenericTableElementModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: DashboardBuilderGenericTableElementModule, imports: [CommonModule, MatTableModule, DashboardBuilderCoreModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: DashboardBuilderGenericTableElementModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, MatTableModule, DashboardBuilderCoreModule],
                    declarations: [GenericTableComponent, GenericTablePropertiesComponent],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { DashboardBuilderGenericTableElementModule };
